import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import { useProducts } from "@/hooks/useProducts";
import { SkeletonLoader } from "./common/SkeletonLoader";

type CategoryFilter = "all" | "focus" | "energy" | "calm";

const ShopSection = () => {
  const { data: products, isLoading } = useProducts();
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
  
  const filteredProducts = activeCategory === "all" 
    ? products || []
    : (products || []).filter(product => product.category === activeCategory);
  
  return (
    <section className="py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 sm:mb-10 md:mb-12 space-y-4 md:space-y-0">
          <div className="text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
              Shop Our <span className="neural-text">Collection</span>
            </h2>
            <p className="text-sm sm:text-base text-solr-starfield-silver max-w-xl mx-auto md:mx-0">
              Discover our range of neural-enhancing beverages, each crafted to optimize 
              different aspects of cognitive performance.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center md:justify-end gap-2">
            <Button 
              onClick={() => setActiveCategory("all")}
              variant="ghost"
              className={`rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm ${
                activeCategory === "all" 
                  ? "bg-solr-nebula-purple/20 text-solr-nebula-purple" 
                  : "text-solr-starfield-silver hover:bg-white/5"
              }`}
            >
              All
            </Button>
            <Button 
              onClick={() => setActiveCategory("focus")}
              variant="ghost"
              className={`rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm ${
                activeCategory === "focus" 
                  ? "bg-solr-nebula-purple/20 text-solr-nebula-purple" 
                  : "text-solr-starfield-silver hover:bg-white/5"
              }`}
            >
              Focus
            </Button>
            <Button 
              onClick={() => setActiveCategory("energy")}
              variant="ghost"
              className={`rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm ${
                activeCategory === "energy" 
                  ? "bg-solr-nebula-purple/20 text-solr-nebula-purple" 
                  : "text-solr-starfield-silver hover:bg-white/5"
              }`}
            >
              Energy
            </Button>
            <Button 
              onClick={() => setActiveCategory("calm")}
              variant="ghost"
              className={`rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm ${
                activeCategory === "calm" 
                  ? "bg-solr-nebula-purple/20 text-solr-nebula-purple" 
                  : "text-solr-starfield-silver hover:bg-white/5"
              }`}
            >
              Calm
            </Button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <SkeletonLoader type="card" count={3} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
        
        <div className="mt-8 sm:mt-10 md:mt-12 text-center">
          <Button className="btn-secondary inline-flex items-center text-sm sm:text-base">
            View All Products
            <ChevronRight className="ml-1 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
